import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import ChatBubble from "./ChatBubble";
import InputBar from "./InputBar";
import { AnimatePresence, motion } from "framer-motion";
import MESSAGE_SENDER_TYPES from "../constants/message-sender-types";
import conversationService from "../services/conversationService";
// import { chatModesMap } from "../config/chatModesMap";

const fnCurrentTimeStamp = () => {
  return new Date().toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });
};

const INITIAL_MESSAGE = {
  id: 1,
  sender: MESSAGE_SENDER_TYPES.BOT,
  message: "Hi there! How can I help you?",
  timestamp: fnCurrentTimeStamp(),
};

const ERROR_MESSAGE =
  "I apologize, but I'm having trouble processing your request. Please try again later.";

export const ChatBot = ({ chatMode, chatModesMap }) => {
  const [messages, setMessages] = useState([INITIAL_MESSAGE]);
  const [isLoading, setIsLoading] = useState(false);
  const chatContainerRef = useRef(null);
  const [dialogId, setDialogId] = useState(null);
  const [threadId, setThreadId] = useState(null);
  const [isFollowupRequired, setIsFollowupRequired] = useState(false);
  const [selectedMessageId, setSelectedMessageId] = useState(null);
  const user = useSelector((state) => state.auth.user);

  const handleMessageSelection = (id) => {
    setSelectedMessageId((prev) => (prev === id ? null : id));
  };
  const API_URL = `${process.env.REACT_APP_API_URL}/chat`;
  const handleSend = async (message, input_type) => {
    const newUserMessage = {
      id: Date.now(),
      sender: MESSAGE_SENDER_TYPES.USER,
      message,
      message_type: input_type,
      timestamp: fnCurrentTimeStamp(),
    };
    setMessages((prev) => [...prev, newUserMessage]);
    setIsLoading(true);
    try {
      const metadata = {
        instance_id: user?.instance_id || chatModesMap[chatMode]?.instance_id,
        application_id: chatModesMap[chatMode]?.application_id,
        first_time_login: true,
        last_active_date_time: new Date().toISOString(),
        modal_time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        is_followup_request: isFollowupRequired,
        user_id: String(user?.user_id || 0),
      };

      dialogId && (metadata["dialog_id"] = dialogId);
      threadId && (metadata["thread_id"] = threadId);

      let request_body;
      let config = {};

      if (message instanceof FormData) {
        message.append("type", input_type);
        message.append("metadata", JSON.stringify(metadata));
        request_body = message;
        config.headers = {
          "Content-Type": "multipart/form-data",
        };
      } else {
        request_body = {
          input_prompt: message,
          type: input_type,
          metadata,
        };
        config.headers = {
          "Content-Type": "application/json",
        };
      }

      const { data } = await axios.post(API_URL, request_body, config);

      if (data && data.success) {
        const response = data.data;
        if (response.followup_required) {
          setDialogId(response.dialog_id);
          setThreadId(response.thread_id);
          setIsFollowupRequired(response.followup_required);
        } else {
          setDialogId("");
          setThreadId("");
          setIsFollowupRequired(false);
        }
        const fullUserMessage = {
          ...newUserMessage,
          instance_id: metadata.instance_id,
          dialog_id: response.dialog_id,
          application_id: metadata.application_id,
          user_id: metadata.user_id,
        };
        await conversationService.conversation(fullUserMessage);

        const botResponse = {
          id: Date.now() + 1,
          sender: MESSAGE_SENDER_TYPES.BOT,
          message: response.response || response.toString(),
          timestamp: fnCurrentTimeStamp(),
          image_id: response.image_id,
          table_id: response.table_id,
          application_id: metadata.application_id,
          dialog_id: response.dialog_id,
          instance_id: metadata.instance_id,
          user_id: metadata.user_id,
        };
        setMessages((prev) => [...prev, botResponse]);
        await conversationService.conversation(botResponse);
      }
    } catch (error) {
      const errorMessage = {
        id: Date.now() + 1,
        sender: MESSAGE_SENDER_TYPES.BOT,
        message: ERROR_MESSAGE,
        timestamp: fnCurrentTimeStamp(),
      };
      setMessages((prev) => [...prev, errorMessage]);
      console.error("Chat API Error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const scrollToBottom = () => {
      if (chatContainerRef.current) {
        chatContainerRef.current.scrollTop =
          chatContainerRef.current.scrollHeight;
      }
    };

    scrollToBottom();
  }, [messages]);

  return (
    <div className="flex flex-col h-full bg-gradient-to-br from-blue-50 to-blue-100 dark:from-gray-800 dark:to-gray-900 border-gray-300 dark:border-gray-600 border-2 transition-colors">
      <main
        ref={chatContainerRef}
        className="flex-grow overflow-y-auto p-4 space-y-2 flex flex-col scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600 scrollbar-track-transparent"
      >
        <AnimatePresence>
          {messages.map((msg) => (
            <ChatBubble
              key={msg.id}
              data={msg}
              selectedMessageId={selectedMessageId}
              onSelect={handleMessageSelection}
              imageId={msg.image_id}
              tableId={msg.table_id}
              dialogId={msg.dialog_id}
            />
          ))}
          {isLoading && <TypingIndicator />}
        </AnimatePresence>
      </main>
      <InputBar onSend={handleSend} disabled={isLoading} />
    </div>
  );
};

const TypingIndicator = () => (
  <motion.div
    initial={{ opacity: 0, scale: 0.9 }}
    animate={{ opacity: 1, scale: 1 }}
    exit={{ opacity: 0, scale: 0.9 }}
    className="self-start bg-gradient-to-br from-gray-200 to-gray-300 dark:from-gray-700 dark:to-gray-800 text-gray-800 dark:text-gray-200 rounded-2xl p-3 shadow-md transition-colors"
  >
    <motion.div
      className="flex space-x-1"
      animate={{
        opacity: [0.4, 1, 0.4],
      }}
      transition={{
        duration: 1.5,
        repeat: Infinity,
      }}
    >
      {[...Array(3)].map((_, i) => (
        <div
          key={i}
          className="w-2 h-2 bg-gray-600 dark:bg-gray-400 rounded-full transition-colors"
        />
      ))}
    </motion.div>
  </motion.div>
);

export default ChatBot;
